import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    Alert,
    useTheme,
    useMediaQuery,
    IconButton,
    InputAdornment,
    Link,
} from '@mui/material';
import { Home as HomeIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import ResetPasswordForm from './ResetPasswordForm';

const LoginForm: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);
    const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { language, setLanguage } = useLanguage();

    const t = (es: string, en: string) => language === 'es' ? es : en;

    const validationSchema = yup.object({
        username: yup.string().required(t('El correo electrónico es requerido', 'Email is required')),
        password: yup.string().required(t('La contraseña es requerida', 'Password is required')),
    });

    const formik = useFormik({
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setError('');
                // Convertir email a minúsculas para login case-insensitive
                const loginData = {
                    username: values.username.toLowerCase().trim(),
                    password: values.password
                };
                const response = await authService.login(loginData);
                // Guardamos el token y el refresh token
                localStorage.setItem('token', response.access_token);
                localStorage.setItem('refreshToken', response.refresh_token);
                // Si la respuesta incluye información del usuario, la guardamos
                if (response.user) {
                    localStorage.setItem('userId', response.user.id.toString());
                    localStorage.setItem('authUser', JSON.stringify(response.user));
                    if (response.user.is_admin) {
                        navigate('/admin/dashboard');
                        return;
                    }
                } else if (response.data && response.data.id) {
                    localStorage.setItem('userId', response.data.id.toString());
                }
                navigate('/dashboard');
            } catch (error: any) {
                console.error('Error al iniciar sesión:', error);
                setError(error.response?.data?.detail || t('Error al iniciar sesión. Por favor, verifica tus credenciales.', 'Login error. Please check your credentials.'));
            }
        },
    });

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: `url('/images/telonbk.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 2, sm: 4 },
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.8) 0%, rgba(78, 205, 196, 0.8) 50%, rgba(69, 183, 209, 0.8) 100%)',
                    zIndex: 1
                }
            }}
        >
            {/* Botón de traducción en la esquina superior izquierda */}
            <IconButton
                onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                sx={{
                    position: 'fixed',
                    top: { xs: 16, sm: 24 },
                    left: { xs: 16, sm: 24 },
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'scale(1.1)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                }}
            >
                <span style={{ fontSize: '24px' }}>
                    {language === 'es' ? '🇺🇸' : '🇪🇸'}
                </span>
            </IconButton>

            {/* Icono de casa en la esquina superior derecha */}
            <IconButton
                onClick={() => navigate('/')}
                sx={{
                    position: 'fixed',
                    top: { xs: 16, sm: 24 },
                    right: { xs: 16, sm: 24 },
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'scale(1.1)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                }}
            >
                <HomeIcon sx={{ 
                    fontSize: { xs: 28, sm: 32 }, 
                    color: '#FF6B6B' 
                }} />
            </IconButton>

            <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2, maxWidth: '400px !important' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            padding: { xs: 3, sm: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: '350px',
                            borderRadius: '25px',
                            border: '4px solid white',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                        }}
                    >
                        <Typography 
                            component="h1" 
                            variant={isMobile ? 'h5' : 'h4'}
                            sx={{ 
                                mb: { xs: 2, sm: 3 },
                                fontWeight: 'bold',
                                color: '#FF6B6B',
                                fontFamily: '"Dancing Script", cursive',
                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                textAlign: 'center'
                            }}
                        >
                            🎓 {t('Iniciar Sesión', 'Login')}
                        </Typography>
                        {error && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mt: 2, 
                                    width: '100%', 
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    borderRadius: '15px',
                                    border: '2px solid #FF6B6B'
                                }}
                            >
                                {error}
                            </Alert>
                        )}
                        <Box
                            component="form"
                            onSubmit={formik.handleSubmit}
                            sx={{ mt: 1, width: '100%' }}
                        >
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label={t('Correo Electrónico', 'Email')}
                                name="username"
                                autoComplete="email"
                                autoFocus
                                value={formik.values.username}
                                onChange={formik.handleChange}
                                error={formik.touched.username && Boolean(formik.errors.username)}
                                helperText={formik.touched.username && formik.errors.username}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#4ECDC4'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        padding: { xs: '12px 14px', sm: '16px 14px' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        '& fieldset': {
                                            borderColor: '#4ECDC4',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#45B7D1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B'
                                        }
                                    }
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label={t('Contraseña', 'Password')}
                                type={showPassword ? "text" : "password"}
                                id="password"
                                autoComplete="current-password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#4ECDC4'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        padding: { xs: '12px 14px', sm: '16px 14px' }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.75rem' }
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        '& fieldset': {
                                            borderColor: '#4ECDC4',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#45B7D1'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6B6B'
                                        }
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ 
                                    mt: { xs: 2, sm: 3 }, 
                                    mb: { xs: 1.5, sm: 2 },
                                    py: { xs: 1.5, sm: 2 },
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    fontWeight: 'bold',
                                    backgroundColor: '#FF6B6B',
                                    borderRadius: '25px',
                                    border: '3px solid white',
                                    boxShadow: '0 8px 20px rgba(255, 107, 107, 0.4)',
                                    '&:hover': {
                                        backgroundColor: '#FF5252',
                                        transform: 'scale(1.02)',
                                        boxShadow: '0 10px 25px rgba(255, 107, 107, 0.6)'
                                    }
                                }}
                            >
                                🚀 {t('Iniciar Sesión', 'Login')}
                            </Button>
                            <Button
                                fullWidth
                                variant="text"
                                onClick={() => navigate('/register')}
                                sx={{
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    color: '#4ECDC4',
                                    fontWeight: 'bold',
                                    borderRadius: '15px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                        color: '#45B7D1'
                                    }
                                }}
                            >
                                🎉 {t('¿No tienes cuenta? Regístrate', "Don't have an account? Register")}
                            </Button>
                            
                            {/* Solo el enlace de olvidaste tu contraseña */}
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => setShowResetPasswordDialog(true)}
                                    sx={{
                                        color: '#4ECDC4',
                                        textDecoration: 'none',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            color: '#45B7D1'
                                        },
                                        display: 'block'
                                    }}
                                >
                                    🔑 {t('¿Olvidaste tu contraseña?', 'Forgot your password?')}
                                </Link>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Container>
            
            {/* Solo el diálogo de reset de contraseña */}
            <ResetPasswordForm 
                open={showResetPasswordDialog}
                onClose={() => setShowResetPasswordDialog(false)}
            />
        </Box>
    );
};

export default LoginForm; 